/**
 * SlideElementEditor Component
 * Comprehensive slide element editor for managing PowerPoint slide references
 * Features:
 * - PowerPoint presentation selection from DAM
 * - Slide index selection with visual picker
 * - Slide preview with navigation
 * - Speaker notes input
 */

import { useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Presentation,
  FolderOpen,
  Upload,
  Trash2,
  ExternalLink,
} from 'lucide-react'
import {
  PowerPointPicker,
  SlideIndexSelector,
  SlidePreview,
  SpeakerNotesInput,
} from './slide'
import { assetService } from '@/lib/api/services/assets'
import type { SlideElementContent } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface SlideElementEditorProps {
  value: Partial<SlideElementContent>
  onChange: (content: Partial<SlideElementContent>) => void
  disabled?: boolean
  className?: string
}

interface SelectedPresentation {
  id: string
  url: string
  thumbnailUrl?: string
  name: string
  fileSize: number
  slideCount?: number
}

// ============================================
// SlideElementEditor Component
// ============================================

export function SlideElementEditor({
  value,
  onChange,
  disabled = false,
  className,
}: SlideElementEditorProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  // Check if we have a presentation selected
  const hasPresentation = !!value.assetId && !!value.assetUrl

  // Get current slide index (default to 0)
  const currentSlideIndex =
    typeof value.slideCount === 'number' && value.slideCount > 0
      ? Math.min(
          Math.max(0, (value as { slideIndex?: number }).slideIndex ?? 0),
          (value.slideCount ?? 1) - 1
        )
      : 0

  // Handle presentation selection from picker
  const handlePresentationSelect = useCallback(
    (presentation: SelectedPresentation) => {
      onChange({
        ...value,
        assetId: presentation.id,
        assetUrl: presentation.url,
        thumbnailUrl: presentation.thumbnailUrl,
        originalFileName: presentation.name,
        fileSize: presentation.fileSize,
        slideCount: presentation.slideCount || 1,
        elementType: 'Slide',
        width: 1920, // Default PowerPoint dimensions
        height: 1080,
        aspectRatio: '16:9',
      })
      setIsPickerOpen(false)
    },
    [value, onChange]
  )

  // Handle slide index change
  const handleSlideIndexChange = useCallback(
    (newIndex: number) => {
      // Create a new object with the slideIndex property
      const updated = {
        ...value,
        slideCount: value.slideCount,
      }
      // Store the slide index - would typically be stored in the content
      ;(updated as SlideElementContent & { slideIndex: number }).slideIndex = newIndex
      onChange(updated)
    },
    [value, onChange]
  )

  // Handle notes change
  const handleNotesChange = useCallback(
    (newNotes: string) => {
      // Create a new object with the notes property
      const updated = {
        ...value,
      }
      // Store the notes - would typically be stored in the content
      ;(updated as SlideElementContent & { notes: string }).notes = newNotes
      onChange(updated)
    },
    [value, onChange]
  )

  // Handle remove presentation
  const handleRemove = useCallback(() => {
    onChange({
      elementType: 'Slide',
    })
  }, [onChange])

  // Format file size
  const formatFileSize = (bytes: number): string => {
    return assetService.formatFileSize(bytes)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Presentation Selection Section */}
      <div className="space-y-4">
        {hasPresentation ? (
          <div className="space-y-4">
            {/* Slide Preview */}
            <SlidePreview
              thumbnailUrl={value.thumbnailUrl}
              slideIndex={currentSlideIndex}
              slideCount={value.slideCount}
              presentationName={value.originalFileName}
              onSlideChange={handleSlideIndexChange}
              disabled={disabled}
            />

            {/* Presentation Info Bar */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <Presentation className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {value.originalFileName || 'Presentation'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                    {value.fileSize && (
                      <span>{formatFileSize(value.fileSize)}</span>
                    )}
                    {value.slideCount && (
                      <>
                        <span>•</span>
                        <span>{value.slideCount} slides</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => window.open(`/assets?assetId=${value.assetId}`, '_blank')}
                disabled={disabled}
                className="text-neutral-500 hover:text-blue-600"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                View
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsPickerOpen(true)}
                disabled={disabled}
                className="rounded-lg"
              >
                <FolderOpen className="h-4 w-4 mr-1.5" />
                Change Presentation
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
                className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Remove
              </Button>
            </div>

            {/* Slide Index Selector */}
            {(value.slideCount ?? 0) > 1 && (
              <>
                <Separator />
                <SlideIndexSelector
                  value={currentSlideIndex}
                  onChange={handleSlideIndexChange}
                  slideCount={value.slideCount}
                  disabled={disabled}
                />
              </>
            )}
          </div>
        ) : (
          /* Empty State */
          <div
            className={cn(
              'p-8 rounded-xl border-2 border-dashed',
              'border-neutral-200 dark:border-neutral-700',
              'bg-neutral-50 dark:bg-neutral-800/30',
              'text-center'
            )}
          >
            <div className="inline-flex p-4 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
              <Presentation className="h-8 w-8 text-orange-500 dark:text-orange-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              No presentation selected
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-sm mx-auto">
              Select a PowerPoint presentation from your asset library to reference a specific slide
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                type="button"
                variant="default"
                onClick={() => setIsPickerOpen(true)}
                disabled={disabled}
                className="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Browse Presentations
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={disabled}
                className="rounded-xl"
                onClick={() => window.open('/assets?tab=upload&type=Presentation', '_blank')}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload New
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Speaker Notes Section */}
      {hasPresentation && (
        <>
          <Separator />
          <SpeakerNotesInput
            value={(value as SlideElementContent & { notes?: string }).notes || ''}
            onChange={handleNotesChange}
            disabled={disabled}
          />
        </>
      )}

      {/* Element Type Info */}
      {hasPresentation && (
        <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-4 text-sm">
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Element Type:</span>{' '}
              <span className="font-medium text-neutral-900 dark:text-white">
                {value.elementType || 'Slide'}
              </span>
            </div>
            {value.width && value.height && (
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Dimensions:</span>{' '}
                <span className="font-medium text-neutral-900 dark:text-white">
                  {value.width} x {value.height}
                </span>
              </div>
            )}
            {value.aspectRatio && (
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Aspect Ratio:</span>{' '}
                <span className="font-medium text-neutral-900 dark:text-white">
                  {value.aspectRatio}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PowerPoint Picker Modal */}
      <PowerPointPicker
        open={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        onSelect={handlePresentationSelect}
      />
    </div>
  )
}

export default SlideElementEditor
